import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./demoFlow.css";

// DemoFlow component - Main component for the demo flow
function DemoPage() {
  const { t } = useTranslation("global");
  const [step, setStep] = useState(1); // 0: Loading, 1: Level Selection, 2: Theme Selection, 3: Questions
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [currentQuestionId, setCurrentQuestionId] = useState(1);
  const [highlightAnimation, setHighlightAnimation] = useState(true);
  const [sectionOpen, setSectionOpen] = useState(true);
  const [questionsDone, setQuestionsDone] = useState({
    1: false,
    2: false,
    3: false,
    4: false,
    5: false
  });
  
  // Create a ref for the theme section
  const themeSelectionRef = useRef(null);
  
  const navigate = useNavigate();
  
  // Simulate periodic animation to guide users
  useEffect(() => {
    const interval = setInterval(() => {
      setHighlightAnimation(prev => !prev);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Add effect to scroll to theme section when level is selected
  useEffect(() => {
    if (step === 2 && themeSelectionRef.current) {
      themeSelectionRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [step]);
  
  // Levels data
  const levels = [
    {
      id: 1,
      name: t("demo.level1", "Niveau 1"),
      description: t("home.niveau1", "Évaluation initiale de maturité pour identifier les points clés d'amélioration"),
      themes: [
        { id: "ssi", name: t("home.1", "SSI"), fullName: t("home.ssi-full", "Sécurité de l'information"), color: "blue", disabled: true, questionCount: 120 },
        { id: "qse", name: t("home.2", "QSE"), fullName: t("home.qse-full", "Qualité, Sécurité & Environnement"), color: "amber", disabled: true, questionCount: 225 },
        { id: "rse", name: t("home.3", "RSE"), fullName: t("home.rse-full", "Responsabilité Sociétale"), color: "green", disabled: true, questionCount: 236 }
      ],
      disabled: true // Mark Niveau 1 as disabled
    },
    {
      id: 2,
      name: t("demo.level2", "Niveau 2"),
      description: t("home.niveau2", "Évaluation approfondie basée sur les normes ISO pour une analyse détaillée"),
      themes: [
        { id: "ssi", name: t("home.1", "SSI"), fullName: t("home.level2-theme1", "Sécurité de l'information (ISO 27001)"), color: "blue", disabled: false, questionCount: 120 },
        { id: "qse", name: t("home.2", "QSE"), fullName: t("home.level2-theme2", "Qualité, Sécurité & Environnement"), color: "amber", disabled: true, questionCount: 225 },
        { id: "rse", name: t("home.3", "RSE"), fullName: t("home.level2-theme3", "Responsabilité Sociétale"), color: "green", disabled: true, questionCount: 236 },
        { id: "rgpd", name: t("home.rgpd", "RGPD"), fullName: t("home.level2-theme4", "Protection des Données"), color: "purple", disabled: true, questionCount: 40 }
      ],
      disabled: false // Niveau 2 is clickable
    }
  ];
  
  // Sections data
  const sections = [
    { id: 1, name: t("demo.section1", "Gouvernance"), questionCount: 9, done: 5, active: false },
    { id: 2, name: t("demo.section2", "Protection Physique"), questionCount: 8, done: 3, active: true },
    { id: 3, name: t("demo.section3", "Opérations"), questionCount: 16, done: 0, active: false },
    { id: 4, name: t("demo.section4", "Contrôle d'Accès"), questionCount: 12, done: 0, active: false },
    { id: 5, name: t("demo.section5", "Continuité d'Activité"), questionCount: 4, done: 0, active: false },
    { id: 6, name: t("demo.section6", "Conformité"), questionCount: 7, done: 0, active: false }
  ];
  
  // Questions data
  const questions = [
    {
      id: 1,
      text: t("demo.question1", "Les locaux de l'entreprise sont-ils équipés de dispositifs de contrôle d'accès physique (ex: badges, biométrie) ?"),
      sectionId: 2
    },
    {
      id: 2,
      text: t("demo.question2", "Existe-t-il un système de surveillance vidéo pour les zones sensibles ?"),
      sectionId: 2
    },
    {
      id: 3,
      text: t("demo.question3", "Caméras de surveillance installées?"),
      sectionId: 2
    },
    {
      id: 4,
      text: t("demo.question4", "Alarme anti-intrusion en place?"),
      sectionId: 2
    },
    {
      id: 5,
      text: t("demo.question5", "Accès restreint avec badge?"),
      sectionId: 2
    }
  ];
  
  // Handle level selection
  const handleLevelSelect = (level) => {
    // Skip if the level is disabled
    if (level.disabled) return;
    
    setSelectedLevel(level);
    setStep(2);
  };
  
  // Handle theme selection
  const handleThemeSelect = (theme) => {
    if (theme.disabled) return;
    setSelectedTheme(theme);
    
    setTimeout(() => {
      navigate("/demo/progress");
    }, 500);
  };
  
  // Handle question navigation
  const handleNextQuestion = () => {
    setQuestionsDone(prev => ({...prev, [currentQuestionId]: true}));
    
    if (currentQuestionId < questions.length) {
      setCurrentQuestionId(currentQuestionId + 1);
    }
  };
  
  const handlePrevQuestion = () => {
    if (currentQuestionId > 1) {
      setCurrentQuestionId(currentQuestionId - 1);
    }
  };
  
  const getCurrentQuestion = () => {
    return questions.find(q => q.id === currentQuestionId) || questions[0];
  };
  
  const getCurrentSection = () => {
    const question = getCurrentQuestion();
    return sections.find(s => s.id === question.sectionId) || sections[0];
  };
  
  const toggleAccordion = () => {
    setSectionOpen(!sectionOpen);
  };
  
  return (
    <div className="bg-gray-50 min-h-screen pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold text-gray-800">{t("demo.title", "Démo ISOeVAL")}</h1>
          <p className="text-gray-600 mt-2">{t("demo.text", "Découvrez notre plateforme d'évaluation de conformité")}</p>
        </div>
      </div>
      
      {/* Step Indicator */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center mb-12">
          <div className={`step-item ${step >= 1 ? 'active' : ''}`}>
            <div className="step-counter">1</div>
            <div className="step-name">{t("demo.level", "Niveau")}</div>
          </div>
          <div className="step-divider"></div>
          <div className={`step-item ${step >= 2 ? 'active' : ''}`}>
            <div className="step-counter">2</div>
            <div className="step-name">{t("demo.theme", "Thème")}</div>
          </div>
          <div className="step-divider"></div>
          <div className={`step-item ${step >= 3 ? 'active' : ''}`}>
            <div className="step-counter">3</div>
            <div className="step-name">{t("demo.questions", "Questions")}</div>
          </div>
        </div>
      </div>
      
      {/* Level Selection */}
      <div className={`container mx-auto px-4 mb-16 ${step > 1 ? 'opacity-70' : ''}`}>
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold text-gray-800">{t("demo.selectLevel", "Sélectionnez un niveau d'évaluation")}</h2>
          <p className="text-gray-600 mt-2">{t("demo.selectLevelDesc", "Choisissez le niveau de détail souhaité pour votre évaluation")}</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {levels.map((level) => (
            <div 
              key={level.id}
              className={`bg-white rounded-lg shadow-md border-2 transition-all duration-300 relative
                ${level.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg cursor-pointer'}
                ${selectedLevel?.id === level.id ? 'border-blue-500' : 'border-transparent'}
                ${level.id === 2 && step === 1 && !selectedLevel ? 'animate-pulse-subtle' : ''}
              `}
              onClick={() => handleLevelSelect(level)}
            >
              {level.id === 2 && step === 1 && !selectedLevel && highlightAnimation && (
                <div className="absolute w-8 h-8 bg-blue-500 animate-ping rounded-full opacity-75 -top-2 -right-2"></div>
              )}
              
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xl">
                    {level.id}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">
                    {level.name}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6">
                  {level.description}
                </p>
                
                <div className="mt-4">
                  <h4 className="font-bold text-lg mb-2">{t("demo.includedThemes", "Thèmes inclus:")}</h4>
                  <div className="flex flex-wrap gap-2">
                    {level.themes.map((theme) => (
                      <span 
                        key={theme.id}
                        className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                          theme.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                          theme.color === 'amber' ? 'bg-amber-100 text-amber-800' :
                          theme.color === 'green' ? 'bg-green-100 text-green-800' :
                          'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {theme.name}
                      </span>
                    ))}
                  </div>
                </div>
                
                {selectedLevel?.id === level.id && (
                  <div className="mt-4 text-blue-600 font-medium flex items-center">
                    <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    {t("demo.selected", "Sélectionné")}
                  </div>
                )}
                
                {level.disabled && (
                  <div className="mt-4 text-gray-500 font-medium flex items-center">
                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                    </svg>
                    {t("demo.notAvailable", "Non disponible pour la démo")}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Theme Selection */}
      {step >= 2 && selectedLevel && (
        <div id="theme-selection" ref={themeSelectionRef} className="container mx-auto px-4 mt-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-800">
              {t("demo.selectTheme", "Sélectionnez un thème d'évaluation")}
            </h2>
            <p className="text-gray-600 mt-2">
              {t("demo.selectThemeDesc", "Choisissez le domaine que vous souhaitez évaluer")}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {selectedLevel.themes.map((theme) => (
              <div 
                key={theme.id}
                className={`bg-white rounded-lg shadow-md transition-all duration-300 overflow-hidden border-2 relative
                  ${theme.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg cursor-pointer'}
                  ${selectedTheme?.id === theme.id ? 'border-blue-500' : 'border-transparent'}
                  ${theme.id === 'ssi' && step === 2 && !selectedTheme ? 'animate-pulse-subtle' : ''}
                `}
                onClick={() => handleThemeSelect(theme)}
              >
                {theme.id === 'ssi' && step === 2 && !selectedTheme && highlightAnimation && (
                  <div className="absolute w-8 h-8 bg-blue-500 animate-ping rounded-full opacity-75 -top-2 -right-2"></div>
                )}
                
                <div className={`h-2 ${
                  theme.color === 'blue' ? 'bg-blue-500' :
                  theme.color === 'amber' ? 'bg-amber-500' :
                  theme.color === 'green' ? 'bg-green-500' :
                  'bg-purple-500'
                }`}></div>
                
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className={`w-10 h-10 rounded-full ${
                      theme.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                      theme.color === 'amber' ? 'bg-amber-100 text-amber-600' :
                      theme.color === 'green' ? 'bg-green-100 text-green-600' :
                      'bg-purple-100 text-purple-600'
                    } flex items-center justify-center font-semibold text-lg mr-3`}>
                      {theme.name}
                    </div>
                    <h3 className="font-bold text-lg text-gray-800">{theme.fullName}</h3>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500 mt-4">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span>{t("demo.duration", "Durée: 1 mois")}</span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500 mt-2">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                    {/* Use the theme's specific question count instead of a level-based count */}
                    <span>{t("demo.questionCount", { count: theme.questionCount })}</span>
                  </div>
                  
                  {selectedTheme?.id === theme.id && (
                    <div className="mt-4 text-blue-600 font-medium flex items-center">
                      <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                      </svg>
                      {t("demo.selected", "Sélectionné")}
                    </div>
                  )}
                </div>
                {theme.disabled && (
                  <div className="mt-4 text-gray-500 font-medium flex items-center">
                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                    </svg>
                    {t("demo.notAvailable", "Non disponible pour la démo")}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Helper tip */}
      <div className="max-w-xl mx-auto mt-16">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">{t("demo.demoGuide", "Guide de démo")}</h3>
              <div className="text-sm text-blue-700">
                {step === 1 && (
                  <p>{t("demo.stepOneGuide", "Commencez par sélectionner un niveau d'évaluation. Pour cette démo, seul le")} <strong>{t("demo.level2", "Niveau 2")}</strong> {t("demo.isAvailable", "est disponible.")}</p>
                )}
                {step === 2 && (
                  <p>{t("demo.stepTwoGuide", "Maintenant, choisissez un thème d'évaluation. Pour cette démo, seul le thème")} <strong>{t("home.1", "SSI")}</strong> {t("demo.themeSSIDetail", "(Sécurité de l'Information) est disponible.")}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    
    </div>
  );
}

export default DemoPage;